/**
 * VOLUME AND THE INCIDENT SHARE — the one chart on this screen with two y-axes.
 *
 * ── WHY THIS PAIR AND NOT ANOTHER ──
 *
 * A second axis is a licence to draw two unrelated lines on one canvas and let
 * the reader assume a relationship. It is justified here and in almost no other
 * place on this screen, for one reason: the two series ARE the same records
 * counted two ways, and the question — "are we getting more incidents, or just
 * more of everything?" — cannot be answered by either alone.
 *
 *   · 40 incidents in a week of 400 entries is 10%.
 *   · 40 incidents in a week of 4,000 entries is 1%.
 *
 * A count chart shows those as the same week. A rate chart shows them as
 * different weeks and hides that one was ten times busier. Both, on one canvas,
 * is the only honest form.
 *
 * ── AND WHY IT IS NOT ON PULSE OR TURNOVER ──
 *
 * Those two deliberately share ONE axis and the reason is recorded in both
 * files: move-ins and move-outs are the same unit (people), and entries and
 * incidents on the pulse are the same unit (records). Giving same-unit series
 * their own scales makes 3 look like 300. That rule is not relaxed here — this
 * panel exists precisely because its two series are NOT the same unit.
 *
 * ── THE RATE IS DERIVED IN THE BROWSER, AND THAT IS SAFE ──
 *
 * Both series come from the SAME batch response, over the same window, bucketed
 * the same way by the same server. Dividing one by the other is arithmetic on
 * two numbers that were counted together — not a join across two requests that
 * might have seen different data.
 *
 * A bucket with no entries gets `null`, never 0%: nothing happened, so there is
 * no share to state, and `connectNulls` is off so the line BREAKS there rather
 * than drawing a confident zero across a quiet weekend.
 */
import React, { useMemo } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { AnalyticsBatchResult } from '../../../../../api/analytics-client';
import { dataViz, palette } from '../../../../../style-utils/theme';
import { ChartShell, ChartShellState } from '../charts/chart-shell';
import { ChartTable } from '../charts/chart-table-view';
import { DashLegend, hiddenKeys } from '../charts/chart-legend';
import { DashTooltip } from '../charts/chart-tooltip';
import {
  BAR_RADIUS,
  SYNC_ID,
  activeDot,
  cursor,
  firstPaintAnimation,
  format,
  grid,
  lineMark,
  margins,
  soloPoint,
  markSeparator,
  xAxisTime,
  yAxisCount,
  yAxisRate,
} from '../charts/chart-theme';
import { readSlot, slotState } from './panel-utils';
import { DrillHandlers } from '../charts/use-chart-drill';

const PANEL_ID = 'rate';

export interface RatePanelProps {
  state: ChartShellState;
  /** The two pulse results, already in the batch. This panel costs no request. */
  entries?: AnalyticsBatchResult;
  incidents?: AnalyticsBatchResult;
  granularity: string;
  seriesOff: string[];
  onToggleSeries: (key: string) => void;
  drill: DrillHandlers;
  onExportPng?: (svg: SVGSVGElement | null) => void;
  onExportCsv?: () => void;
}

interface Point {
  key: string;
  label: string;
  entries: number;
  incidents: number;
  /** Null, never zero, when there were no entries to be a share of. */
  rate: number | null;
}

export const RatePanel: React.FC<RatePanelProps> = ({
  state,
  entries,
  incidents,
  granularity,
  seriesOff,
  onToggleSeries,
  drill,
  onExportPng,
  onExportCsv,
}) => {
  const slot = readSlot(entries);
  const data = slot.ok;

  const incidentsByKey = useMemo(() => {
    const map = new Map<string, number>();
    if (!incidents || 'error' in incidents) return map;
    for (const item of incidents.items) {
      map.set(item.key, format.numberOf(item.value) ?? 0);
    }

    return map;
  }, [incidents]);

  const points = useMemo<Point[]>(
    () =>
      (data?.items || []).map(item => {
        const total = format.numberOf(item.value) ?? 0;
        const bad = incidentsByKey.get(item.key) ?? 0;

        return {
          key: item.key,
          label: format.dayKey(item.key),
          entries: total,
          incidents: bad,
          rate: total > 0 ? Math.round((bad / total) * 1000) / 10 : null,
        };
      }),
    [data, incidentsByKey]
  );

  const hidden = hiddenKeys(seriesOff, PANEL_ID);
  const bucket = granularity === 'week' ? 'week' : 'day';

  const overall = points.reduce(
    (sum, point) => ({
      entries: sum.entries + point.entries,
      incidents: sum.incidents + point.incidents,
    }),
    { entries: 0, incidents: 0 }
  );
  const overallRate =
    overall.entries > 0
      ? Math.round((overall.incidents / overall.entries) * 1000) / 10
      : null;

  return (
    <ChartShell
      id={PANEL_ID}
      title="Volume against the incident share"
      wide
      aspect={2.6}
      minHeight={280}
      state={slotState(state, slot)}
      notes={slot.notes}
      caveat={`Bars are every record logged per ${bucket}, on the left axis. The line is the share of those records that carried an incident severity, on the right axis, as a percentage. They answer different halves of one question: forty incidents in four hundred records and forty in four thousand are the same bar and a very different line.`}
      emptyReason={
        slot.empty
          ? {
              title: 'Nothing was recorded in this period',
              text: 'With no records there is no volume to plot and no share to take of it.',
            }
          : null
      }
      summary={
        points.length
          ? `Volume and incident share by ${bucket}: ${overall.entries.toLocaleString()} records, ${overall.incidents.toLocaleString()} carrying an incident${
              overallRate == null ? '' : `, ${overallRate}% overall`
            }.`
          : undefined
      }
      onExportPng={onExportPng}
      onExportCsv={onExportCsv}
      controls={
        <DashLegend
          panelId={PANEL_ID}
          entries={[
            { key: 'entries', label: 'Records', tone: dataViz.series[0] },
            { key: 'rate', label: 'Incident share', tone: palette.warning },
          ]}
          seriesOff={seriesOff}
          onToggle={onToggleSeries}
        />
      }
      table={
        data ? (
          <ChartTable
            dimensionLabel={format.humanise(bucket)}
            valueLabel="Records"
            rows={points.map(point => ({
              key: point.key,
              label: point.label,
              value: point.entries.toLocaleString(),
              compareValue: point.incidents.toLocaleString(),
              // NOT "0%" for a quiet bucket. See the header.
              delta: point.rate == null ? 'no records' : `${point.rate}%`,
            }))}
            showCompare
            compareLabel="With an incident"
            deltaLabel="Share"
          />
        ) : undefined
      }
    >
      <ComposedChart accessibilityLayer data={points} margin={margins.dualAxis} syncId={SYNC_ID}>
        <CartesianGrid {...grid} />
        <XAxis dataKey="label" {...xAxisTime} />

        {/* TWO AXES, EACH LABELLED WITH ITS OWN UNIT. An unlabelled second axis
            is the thing that makes a dual-axis chart a lie: the reader cannot
            tell which series it belongs to. */}
        <YAxis yAxisId="left" {...yAxisCount} />
        <YAxis yAxisId="right" orientation="right" {...yAxisRate} />

        <Tooltip
          content={
            <DashTooltip
              series={[
                {
                  key: 'entries',
                  label: 'records',
                  tone: dataViz.series[0],
                  noun: ['record', 'records'],
                },
                { key: 'incidents', label: 'with an incident', tone: palette.warning },
              ]}
              hidden={hidden}
              note={row =>
                row.rate == null ? (
                  <span>
                    No records in this {bucket}, so there is no share to state — the
                    line breaks here rather than showing 0%.
                  </span>
                ) : (
                  <span>
                    <strong>{String(row.rate)}%</strong> of this {bucket}&apos;s records
                    carried an incident.
                  </span>
                )
              }
            />
          }
          cursor={cursor.bar}
        />

        {!hidden.includes('entries') ? (
          <Bar
            yAxisId="left"
            dataKey="entries"
            name="Records"
            fill={dataViz.series[0]}
            radius={BAR_RADIUS}
            {...markSeparator}
            onClick={drill.onMark}
            cursor="pointer"
            {...firstPaintAnimation(PANEL_ID)}
          />
        ) : null}

        {!hidden.includes('rate') ? (
          <Line
            yAxisId="right"
            dataKey="rate"
            name="Incident share"
            stroke={palette.warning}
            {...lineMark}
            {...soloPoint(points.length, palette.warning)}
            /* THE LINE BREAKS ON A QUIET BUCKET. `connectNulls` would draw a
               straight, confident segment across a weekend nobody recorded
               anything in — a rate for a period that has no denominator. */
            connectNulls={false}
            activeDot={activeDot(palette.warning)}
            {...firstPaintAnimation(`${PANEL_ID}-line`)}
          />
        ) : null}
      </ComposedChart>
    </ChartShell>
  );
};
